import type { DocumentBlock, LegalDocument } from '@/schemas/schema';

/**
 * Render a `LegalDocument` (the structured block tree stored on
 * `OrgContractDocument.Document`) as plain text suitable for AI input.
 *
 * Walks articles → sections → paragraphs / list_items / definition_items.
 * Joins blocks with blank lines so the agent can recognise paragraph
 * boundaries without HTML / Markdown ambiguity.
 */
export function documentToText(document: LegalDocument): string {
  const lines: string[] = [];

  const title = document.metadata?.title?.trim();
  if (title) {
    lines.push(title.toUpperCase());
    lines.push('');
  }

  for (const block of document.content ?? []) {
    appendBlock(block, lines, 0);
  }

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

function appendBlock(block: DocumentBlock, lines: string[], depth: number): void {
  switch (block.type) {
    case 'article': {
      const number = typeof block.props?.number === 'string' ? block.props.number : '';
      const articleTitle = typeof block.props?.title === 'string' ? block.props.title : '';
      const header = [number ? `ARTICLE ${number}` : 'ARTICLE', articleTitle]
        .filter(Boolean)
        .join(' — ');
      if (header) {
        lines.push('');
        lines.push(header);
      }
      for (const child of block.children ?? []) {
        appendBlock(child, lines, depth + 1);
      }
      break;
    }
    case 'section': {
      const number = typeof block.props?.number === 'string' ? block.props.number : '';
      const sectionTitle = typeof block.props?.title === 'string' ? block.props.title : '';
      const header = [number, sectionTitle].filter(Boolean).join(' ');
      if (header) {
        lines.push('');
        lines.push(header);
      }
      for (const child of block.children ?? []) {
        appendBlock(child, lines, depth + 1);
      }
      break;
    }
    case 'paragraph': {
      if (block.text) {
        lines.push('');
        lines.push(block.text);
      }
      break;
    }
    case 'list': {
      lines.push('');
      for (const child of block.children ?? []) {
        appendBlock(child, lines, depth + 1);
      }
      break;
    }
    case 'list_item': {
      if (block.text) lines.push(`- ${block.text}`);
      break;
    }
    case 'definition': {
      lines.push('');
      for (const child of block.children ?? []) {
        appendBlock(child, lines, depth + 1);
      }
      break;
    }
    case 'definition_item': {
      const term = typeof block.props?.term === 'string' ? block.props.term : '';
      if (term && block.text) lines.push(`${term}: ${block.text}`);
      else if (block.text) lines.push(block.text);
      break;
    }
    case 'table':
    case 'table_row':
    case 'table_cell': {
      for (const child of block.children ?? []) {
        appendBlock(child, lines, depth + 1);
      }
      if (block.text) lines.push(block.text);
      break;
    }
    default: {
      if (block.text) lines.push(block.text);
      for (const child of block.children ?? []) {
        appendBlock(child, lines, depth + 1);
      }
    }
  }
}
